// 员工案件服务
import { supabase } from '../supabase/client';
import { BlacklistGuard } from './blacklistGuard';
import { PAYMENT_CONFIG } from '../../config/payment';

/** 財務趨勢／統計用：payments 欄位（含 id 供去重） */
const COMPANY_FINANCE_PAYMENT_SELECT =
  'id, request_id, payment_method, paid_amount, deposit_amount, remaining_amount, payment_type, amount, craftsman_earnings, paid_at, created_at, finance_review_status, status';

/** 與財務儀表板現金流趨勢一致：單筆實收金額（優先 paid_amount） */
export function companyFinancePaymentReceivedAmount(p: {
  paid_amount?: number | null;
  deposit_amount?: number | null;
  remaining_amount?: number | null;
  payment_type?: string | null;
  amount?: number | null;
}): number {
  if (p.paid_amount != null && Number(p.paid_amount) > 0) {
    return Number(p.paid_amount);
  }
  if (p.payment_type === 'deposit' && p.deposit_amount != null && Number(p.deposit_amount) > 0) {
    return Number(p.deposit_amount);
  }
  if (p.payment_type === 'additional' && p.amount != null && Number(p.amount) > 0) {
    return Number(p.amount);
  }
  if (p.payment_type === 'remaining' && p.remaining_amount != null && Number(p.remaining_amount) > 0) {
    return Number(p.remaining_amount);
  }
  return Number(p.amount || 0);
}

/** 與佣金頁入帳認列一致：單筆扣佣後淨額（優先 craftsman_earnings，按已付比例分攤） */
export function companyFinancePaymentNetAmount(p: {
  paid_amount?: number | null;
  deposit_amount?: number | null;
  remaining_amount?: number | null;
  payment_type?: string | null;
  amount?: number | null;
  craftsman_earnings?: number | null;
}): number {
  const amount = Math.max(Number(p.amount || 0), 0);
  const paid = Number(p.paid_amount || 0);
  const paidBasis = paid > 0
    ? Math.min(paid, amount > 0 ? amount : paid)
    : p.payment_type === 'deposit'
      ? Math.max(Number(p.deposit_amount || 0), 0)
      : p.payment_type === 'remaining'
        ? Math.max(Number(p.remaining_amount || 0), 0)
        : amount;

  const net = Number(p.craftsman_earnings || 0);
  if (net > 0) {
    if (amount > 0 && paidBasis > 0) {
      return (net * paidBasis) / amount;
    }
    return net;
  }

  return companyFinancePaymentReceivedAmount(p);
}

/** 歸屬日：優先 paid_at，與 getCompanyFinanceEarningsTrend 一致 */
export function companyFinancePaymentRecognizedAt(p: {
  paid_at?: string | null;
  created_at?: string | null;
}): Date | null {
  const raw = p.paid_at || p.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * 損益／財報：在 [start, end]（含起訖）依認列日加總扣佣後淨額。
 * 與 getCompanyEarnings、公司收入趨勢同口徑；傳入列應已為財務核准付款（getCompanyApprovedFinancePaymentRows）。
 */
export function sumCompanyRecognizedNetRevenueInRange(
  paymentRows: any[] | null | undefined,
  start: Date,
  end: Date
): number {
  const startMs = start.getTime();
  const endMs = end.getTime();
  let sum = 0;
  for (const p of paymentRows || []) {
    const d = companyFinancePaymentRecognizedAt(p);
    if (!d) continue;
    const t = d.getTime();
    if (t < startMs || t > endMs) continue;
    sum += companyFinancePaymentNetAmount(p);
  }
  return sum;
}

/**
 * 合併兩次查詢的付款列（依 id 去重）。
 * 第二次查詢可能帶入 service_requests 嵌套，會剝除以免影響後續欄位讀取。
 */
function mergePaymentRowsById(
  rowsA: any[] | null | undefined,
  rowsB: any[] | null | undefined
): any[] {
  const map = new Map<string, any>();
  const ingest = (rows: any[] | null | undefined) => {
    for (const row of rows || []) {
      const id = row?.id as string | undefined;
      if (!id || map.has(id)) continue;
      const { service_requests: _sr, ...rest } = row;
      map.set(id, rest);
    }
  };
  ingest(rowsA);
  ingest(rowsB);
  return Array.from(map.values());
}

const ATTACHMENT_BUCKET = 'service-attachments';
const SERVICE_REQUEST_SELECT_FIELDS = `
  id,
  title,
  description,
  status,
  estimated_price,
  fixed_total_price,
  pricing_mode,
  created_at,
  scheduled_date,
  started_at,
  completed_at,
  location,
  urgency,
  notes,
  assigned_department,
  site_contact_name,
  site_contact_phone,
  schedule_notes,
  completion_photos,
  assigned_employee_id,
  user_id,
  customer_name,
  customer_phone,
  schedule:service_request_schedule(
    scheduled_start_at,
    scheduled_end_at,
    assigned_department,
    assigned_employee_id,
    site_contact_name,
    site_contact_phone,
    notes,
    created_at,
    updated_at
  ),
  customer:users!service_requests_user_id_fkey(
    id,
    name,
    email,
    phone
  )
`;

const SERVICE_REQUEST_SELECT_FIELDS_FALLBACK = `
  id,
  title,
  description,
  status,
  estimated_price,
  fixed_total_price,
  pricing_mode,
  created_at,
  scheduled_date,
  started_at,
  completed_at,
  location,
  urgency,
  notes,
  assigned_department,
  site_contact_name,
  site_contact_phone,
  schedule_notes,
  completion_photos,
  assigned_employee_id,
  user_id,
  customer_name,
  customer_phone,
  schedule:service_request_schedule(
    scheduled_start_at,
    scheduled_end_at,
    assigned_department,
    assigned_employee_id,
    site_contact_name,
    site_contact_phone,
    notes,
    created_at,
    updated_at
  ),
  customer:users!service_requests_user_id_fkey(
    id,
    name,
    email,
    phone
  )
`;

const IGNORED_SCHEMA_ERROR_CODES = new Set(['42P01', '42703', 'PGRST205']);

type ServiceRequestSelector = (fields: string) => Promise<{ data: any; error: any }>;

const selectServiceRequestsWithFallback = async (
  selector: ServiceRequestSelector
): Promise<{ data: any; error: any }> => {
  let response = await selector(SERVICE_REQUEST_SELECT_FIELDS);
  if (response.error && response.error.code === '42703') {
    response = await selector(SERVICE_REQUEST_SELECT_FIELDS_FALLBACK);
  }
  return response;
};

const fetchAssignedDepartments = async (serviceRequestId: string): Promise<string[]> => {
  const { data, error } = await supabase
    .from('service_requests')
    .select('assigned_departments')
    .eq('id', serviceRequestId)
    .maybeSingle();

  if (error && error.code === '42703') {
    return [];
  }

  if (error && !IGNORED_SCHEMA_ERROR_CODES.has(error.code ?? '')) {
    console.error('Failed to fetch assigned_departments:', error);
  }

  if (Array.isArray(data?.assigned_departments)) {
    return data.assigned_departments.filter((dept: any) => typeof dept === 'string' && dept.trim().length > 0);
  }

  return [];
};

const updateServiceRequestWithDepartments = async (
  serviceRequestId: string,
  updateData: Record<string, any>,
  departments?: string[]
): Promise<any> => {
  const updatePayload =
    departments && departments.length > 0
      ? {
          ...updateData,
          assigned_departments: departments
        }
      : updateData;

  let { error } = await supabase
    .from('service_requests')
    .update(updatePayload)
    .eq('id', serviceRequestId);

  if (error && error.code === '42703' && updatePayload.assigned_departments !== undefined) {
    const fallbackPayload = { ...updatePayload };
    delete fallbackPayload.assigned_departments;

    ({ error } = await supabase
      .from('service_requests')
      .update(fallbackPayload)
      .eq('id', serviceRequestId));
  }

  return { error };
};

export interface EmployeeJob {
  id: string;
  title: string;
  description: string;
  status: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  amount?: number;
  fixedTotalPrice?: number | null;
  pricingMode?: 'hourly' | 'company_fixed';
  depositAmount?: number;
  remainingAmount?: number;
  // ✅ 添加付款相關字段（與 getCompanyJobs 保持一致）
  receivedDepositAmount?: number;
  pendingDepositAmount?: number;
  payment?: any;
  paymentStatus?: string | null;
  financeReviewStatus?: 'pending' | 'approved' | 'rejected';
  paidAt?: Date | null;
  created_at: Date;
  scheduled_date?: Date;
  started_at?: Date;
  completed_at?: Date;
  location: any;
  urgency: string;
  notes?: string | null;
  assigned_department?: string | null;
  assigned_departments?: string[];
  site_contact_name?: string | null;
  site_contact_phone?: string | null;
  schedule_notes?: string | null;
  completion_photos?: string[] | null;
  assigned_at?: Date;
  schedule?: JobSchedule | JobSchedule[] | null;
}

export interface WarrantyFollowUp {
  id: string;
  service_request_id: string;
  employee_id: string | null;
  note: string;
  next_follow_up_date: string | null;
  followup_type?: 'phone' | 'onsite' | 'line' | 'email' | 'other' | null;
  customer_response?: 'satisfied' | 'minor_issue' | 'major_issue' | 'no_response' | null;
  attachments?: string[];
  created_at: string;
  employee?: {
    id: string;
    name: string | null;
    department: string | null;
  };
}

export interface JobSchedule {
  id: string;
  service_request_id: string;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  assigned_department: string | null;
  assigned_employee_id: string | null;
  site_contact_name: string | null;
  site_contact_phone: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobAttachment {
  id: string;
  service_request_id: string;
  employee_id: string | null;
  file_url: string;
  storage_path: string | null;
  file_type: string | null;
  file_name: string | null;
  description: string | null;
  created_at: string;
}

export class EmployeeJobService {
  // 获取分配给员工的所有案件
  static async getEmployeeJobs(employeeId: string): Promise<{
    jobs: EmployeeJob[];
    stats: {
      pending: number;
      confirmed: number;
      inProgress: number;
      completed: number;
      cancelled: number;
      total: number;
      unassigned?: number;
    };
  }> {
    try {
      // 首先通过 user_id 查找员工记录
      let employeeData = null;
      let empError = null;
      
      // 尝试通过 user_id 查询（使用 .limit() 處理可能有多筆記錄的情況）
      let cachedUserType: string | null = null;

      const { data: empDataByUserIdList, error: errorByUserId } = await supabase
        .from('employees')
        .select('id, user_id, department, company_id') // ✅ 添加 company_id
        .eq('user_id', employeeId)
        .limit(10);
      
      if (!errorByUserId && empDataByUserIdList && empDataByUserIdList.length > 0) {
        // 如果有多筆記錄，使用第一筆
        employeeData = empDataByUserIdList[0];
        if (empDataByUserIdList.length > 1) {
          console.log(`⚠️ getEmployeeJobs: 通過 user_id 找到 ${empDataByUserIdList.length} 筆員工記錄，使用第一筆`);
        }
      } else {
        // 如果通过 user_id 找不到，尝试通过 email 查询（需要先获取用户 email）
        const { data: userData } = await supabase
          .from('users')
          .select('email, user_type')
          .eq('id', employeeId)
          .maybeSingle();
        
        if (userData?.email) {
          cachedUserType = userData.user_type ?? null;
          // 使用 .limit(1) 來處理可能有多筆記錄的情況
          const { data: empDataByEmailList, error: errorByEmail } = await supabase
            .from('employees')
            .select('id, user_id, department')
            .eq('email', userData.email)
            .limit(10);
          
          if (!errorByEmail && empDataByEmailList && empDataByEmailList.length > 0) {
            // 優先選擇 user_id 匹配的記錄
            let empDataByEmail = empDataByEmailList.find(emp => emp.user_id === employeeId);
            
            // 如果沒有匹配的，使用第一筆記錄
            if (!empDataByEmail) {
              empDataByEmail = empDataByEmailList[0];
              console.log(`⚠️ 通过 email 找到 ${empDataByEmailList.length} 筆員工記錄，但 user_id 都不匹配，使用第一筆`);
            } else {
              console.log(`✅ 通过 email 找到 ${empDataByEmailList.length} 筆員工記錄，使用 user_id 匹配的記錄`);
            }
            
            employeeData = empDataByEmail;
          } else {
            empError = errorByEmail;
          }
        } else {
          empError = errorByUserId;
        }
      }

      if (empError || !employeeData) {
        console.error('找不到员工记录:', empError || '没有匹配的记录');
        console.warn('用户ID:', employeeId);
        
        // 添加更詳細的調試資訊
        console.warn('🔍 調試資訊:');
        console.warn('1. 嘗試通過 user_id 查詢 employees 表');
        if (errorByUserId) {
          console.warn('   - 查詢錯誤:', errorByUserId);
          console.warn('   - 錯誤代碼:', errorByUserId.code);
          console.warn('   - 錯誤訊息:', errorByUserId.message);
        } else {
          console.warn('   - 查詢成功，但沒有找到記錄');
        }
        
        // 檢查 users 表
        try {
          const { data: userCheck, error: userCheckError } = await supabase
            .from('users')
            .select('id, email, user_type')
            .eq('id', employeeId)
            .maybeSingle();
          
          if (userCheck) {
            console.warn('2. users 表中找到用戶:', {
              id: userCheck.id,
              email: userCheck.email,
              user_type: userCheck.user_type
            });
            
            // 嘗試通過 email 查詢 employees 表
            if (userCheck.email) {
              const { data: empByEmail, error: empByEmailError } = await supabase
                .from('employees')
                .select('id, user_id, email, department, company_id')
                .eq('email', userCheck.email)
                .limit(5);
              
              if (empByEmail && empByEmail.length > 0) {
                console.warn('3. 通過 email 找到員工記錄:', empByEmail.map(emp => ({
                  id: emp.id,
                  email: emp.email,
                  user_id: emp.user_id,
                  department: emp.department,
                  company_id: emp.company_id,
                  matches_user_id: emp.user_id === employeeId
                })));
              } else if (empByEmailError) {
                console.warn('3. 通過 email 查詢失敗:', empByEmailError);
                console.warn('   錯誤代碼:', empByEmailError.code);
                console.warn('   錯誤訊息:', empByEmailError.message);
              } else {
                console.warn('3. 通過 email 查詢成功，但沒有找到記錄');
              }
            }
          } else if (userCheckError) {
            console.warn('2. users 表查詢失敗:', userCheckError);
            console.warn('   錯誤代碼:', userCheckError.code);
            console.warn('   錯誤訊息:', userCheckError.message);
          } else {
            console.warn('2. users 表中也沒有找到用戶記錄');
          }
        } catch (error) {
          console.error('調試查詢失敗:', error);
        }
        
        // ✅ 特殊处理：对于 other_staff 类型，即使没有员工记录，也尝试查询部门分配的任务
        let userType: string | null = cachedUserType;
        if (!userType) {
          const { data: userData } = await supabase
            .from('users')
            .select('email, user_type')
            .eq('id', employeeId)
            .maybeSingle();
          userType = userData?.user_type ?? null;
        }
        
        // ✅ 如果是 other_staff 类型但没有员工记录，返回空案件列表（确保数据安全）
        // 注意：没有员工记录时无法确定 company_id，为了安全起见不返回任何案件
        if (userType === 'other_staff') {
          console.warn('⚠️ 未找到員工記錄，但用戶類型為 other_staff。無法確定公司ID，返回空案件列表以確保數據安全。');
          return { jobs: [], stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0, total: 0 } };
        }
        
        return { jobs: [], stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0, total: 0 } };
      }

      const actualEmployeeId = employeeData.id;
      const currentDepartment = (employeeData.department ?? '').trim() || null;
      const employeeCompanyId = employeeData.company_id || null; // ✅ 获取员工所属的公司ID

      let userType: string | null = cachedUserType;
      if (!userType) {
        const { data: userData } = await supabase
          .from('users')
          .select('email, user_type')
          .eq('id', employeeId)
          .maybeSingle();
        userType = userData?.user_type ?? null;
      }

      // 获取分配给该员工的所有案件
      const { data: jobsData, error } = await selectServiceRequestsWithFallback((fields) =>
        supabase
          .from('service_requests')
          .select(fields)
          .eq('assigned_employee_id', actualEmployeeId)
          .order('created_at', { ascending: false })
      );

      if (error) {
        console.error('获取员工案件失败:', error);
        return { jobs: [], stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0, total: 0 } };
      }

      const jobMap = new Map<string, any>();
      (jobsData || []).forEach((job: any) => {
        if (job?.id) {
          jobMap.set(job.id, job);
        }
      });

      const { data: assignmentRecords, error: assignmentError } = await supabase
        .from('service_request_assignments')
        .select('service_request_id')
        .eq('employee_id', actualEmployeeId);

      if (!assignmentError && assignmentRecords?.length) {
        const additionalJobIds = assignmentRecords
          .map((record: any) => record?.service_request_id)
          .filter((id: string | null | undefined) => !!id && !jobMap.has(id));

        if (additionalJobIds.length > 0) {
          const { data: additionalJobs, error: additionalError } = await selectServiceRequestsWithFallback((fields) =>
            supabase
              .from('service_requests')
              .select(fields)
              .in('id', additionalJobIds)
          );

          if (additionalError) {
            console.error('獲取指派案件失敗:', additionalError);
          } else {
            (additionalJobs || []).forEach((job: any) => {
              if (job?.id && !jobMap.has(job.id)) {
                jobMap.set(job.id, job);
              }
            });
          }
        }
      }

      // 對於特定部門員工，也需要查詢分配給該部門的案件
      const shouldLoadByDepartment = userType === 'administrative_staff' || 
                                     userType === 'finance_staff' || 
                                     userType === 'customer_service' ||
                                     userType === 'other_staff' ||  // ✅ 添加：支持其他部门
                                     (userType === 'employee' && currentDepartment === '維修部');
      const departmentTargets = new Set<string>();
      if (shouldLoadByDepartment) {
        if (userType === 'administrative_staff') {
          departmentTargets.add('行政部');
        } else if (userType === 'finance_staff') {
          departmentTargets.add('財務部');
        } else if (userType === 'customer_service') {
          departmentTargets.add('客服部');
        } else if (userType === 'other_staff') {  // ✅ 新增：处理其他部门
          departmentTargets.add('其他');
        } else if (userType === 'employee' && currentDepartment === '維修部') {
          departmentTargets.add('維修部');
        }
        // 确保添加当前部门（如果存在且不在上面的条件中）
        if (currentDepartment && !departmentTargets.has(currentDepartment)) {
          departmentTargets.add(currentDepartment);
        }
      }

      if (shouldLoadByDepartment && departmentTargets.size > 0) {
        console.log('🔍 查詢部門分配的案件:', {
          userType,
          currentDepartment,
          departmentTargets: Array.from(departmentTargets)
        });
        
        // ✅ 修复：在 selectServiceRequestsWithFallback 的回调中使用 fields
        const { data: departmentJobs, error: departmentError } = await selectServiceRequestsWithFallback(
          (fields) => {
            let departmentQuery = supabase
              .from('service_requests')
              .select(fields)
              .overlaps('assigned_departments', Array.from(departmentTargets));
            
            // 如果员工有 company_id，添加公司限制
            if (employeeCompanyId) {
              departmentQuery = departmentQuery.eq('company_id', employeeCompanyId);
            }
            
            return departmentQuery.order('created_at', { ascending: false });
          }
        );

        if (departmentError) {
          if (departmentError.code === '42703') {
            // 如果 assigned_departments 列不存在，使用 assigned_department
            const { data: fallbackDepartmentJobs, error: fallbackError } = await selectServiceRequestsWithFallback(
              (fields) => {
                let fallbackDepartmentQuery = supabase
                  .from('service_requests')
                  .select(fields)
                  .in('assigned_department', Array.from(departmentTargets));
                
                // 如果员工有 company_id，添加公司限制
                if (employeeCompanyId) {
                  fallbackDepartmentQuery = fallbackDepartmentQuery.eq('company_id', employeeCompanyId);
                }
                
                return fallbackDepartmentQuery.order('created_at', { ascending: false });
              }
            );

            if (fallbackError && !IGNORED_SCHEMA_ERROR_CODES.has(fallbackError.code ?? '')) {
              console.error('依照部門獲取案件失敗:', fallbackError);
            } else {
              console.log('✅ 通過 assigned_department 找到', fallbackDepartmentJobs?.length || 0, '個案件');
              (fallbackDepartmentJobs || []).forEach((job: any) => {
                if (job?.id && !jobMap.has(job.id)) {
                  jobMap.set(job.id, job);
                }
              });
            }
          } else if (!IGNORED_SCHEMA_ERROR_CODES.has(departmentError.code ?? '')) {
            console.error('依照部門獲取案件失敗:', departmentError);
          }
        } else {
          console.log('✅ 通過 assigned_departments 找到', departmentJobs?.length || 0, '個案件');
          (departmentJobs || []).forEach((job: any) => {
            if (job?.id && !jobMap.has(job.id)) {
              jobMap.set(job.id, job);
            }
          });
        }
        
        // ✅ 新增：對於維修部員工，額外查詢 assigned_employee_id 的已完成案件
        // 這是為了處理在修改代碼之前完成的案件（assigned_departments 中沒有「維修部」）
        if (userType === 'employee' && currentDepartment === '維修部') {
          console.log('🔍 維修部員工：額外查詢 assigned_employee_id 的已完成案件');
          const { data: completedJobsByEmployee, error: completedError } = await selectServiceRequestsWithFallback(
            (fields) => {
              let completedQuery = supabase
                .from('service_requests')
                .select(fields)
                .eq('assigned_employee_id', actualEmployeeId)
                .eq('status', 'completed');
              
              // 如果员工有 company_id，添加公司限制
              if (employeeCompanyId) {
                completedQuery = completedQuery.eq('company_id', employeeCompanyId);
              }
              
              return completedQuery.order('created_at', { ascending: false });
            }
          );
          
          if (!completedError && completedJobsByEmployee) {
            console.log('✅ 維修部員工：通過 assigned_employee_id 找到', completedJobsByEmployee.length, '個已完成案件');
            completedJobsByEmployee.forEach((job: any) => {
              if (job?.id && !jobMap.has(job.id)) {
                jobMap.set(job.id, job);
              }
            });
          } else if (completedError && !IGNORED_SCHEMA_ERROR_CODES.has(completedError.code ?? '')) {
            console.error('維修部員工查詢已完成案件失敗:', completedError);
          }
        }
        
        // ✅ 新增：对于财务员工，除了部门分配的案件，还要查询所有统包案件
        if (userType === 'finance_staff' && employeeCompanyId) {
          console.log('🔍 財務員工：額外查詢所有統包案件');
          const { data: allTurnkeyJobs, error: turnkeyError } = await selectServiceRequestsWithFallback(
            (fields) => {
              return supabase
                .from('service_requests')
                .select(fields)
                .eq('company_id', employeeCompanyId)
                .eq('pricing_mode', 'company_fixed')
                .order('created_at', { ascending: false });
            }
          );
          
          if (!turnkeyError && allTurnkeyJobs) {
            console.log('✅ 財務員工：找到', allTurnkeyJobs.length, '個統包案件');
            allTurnkeyJobs.forEach((job: any) => {
              if (job?.id && !jobMap.has(job.id)) {
                jobMap.set(job.id, job);
              }
            });
          } else if (turnkeyError && !IGNORED_SCHEMA_ERROR_CODES.has(turnkeyError.code ?? '')) {
            console.error('財務員工查詢統包案件失敗:', turnkeyError);
          }
        }
      }

      const mergedJobs = Array.from(jobMap.values());

      // ✅ 查詢付款記錄（與 getCompanyJobs 保持一致）
      const jobIds = mergedJobs.map(job => job.id);
      let paymentsByJob: Record<string, any> = {};
      if (jobIds.length > 0) {
        const { data: payments, error: paymentsError } = await supabase
          .from('payments')
          .select('id, request_id, status, paid_amount, deposit_amount, remaining_amount, payment_type, paid_at, payment_method, finance_review_status, reviewed_at, reviewed_by, created_at, updated_at')
          .in('request_id', jobIds);

        if (paymentsError) {
          console.error('Error fetching payments in getEmployeeJobs:', paymentsError);
        } else {
          paymentsByJob = (payments || []).reduce<Record<string, any>>((acc, payment) => {
            if (payment.request_id) {
              acc[payment.request_id] = payment;
            }
            return acc;
          }, {});
        }
      }

      // ✅ 批次查詢缺失的客戶資訊（如果 customer join 失敗）
      const missingCustomerJobIds = mergedJobs
        .filter((job: any) => !job.customer && job.user_id)
        .map((job: any) => job.user_id);
      
      let customersByUserId: Record<string, any> = {};
      if (missingCustomerJobIds.length > 0) {
        const uniqueUserIds = [...new Set(missingCustomerJobIds)];
        try {
          const { data: usersData, error: usersError } = await supabase
            .from('users')
            .select('id, name, email, phone')
            .in('id', uniqueUserIds);
          
          if (usersError) {
            console.warn('⚠️ 批次查詢客戶資訊失敗:', usersError.message);
          } else if (usersData) {
            usersData.forEach(user => {
              customersByUserId[user.id] = user;
            });
            console.log(`✅ 批次查詢到 ${usersData.length} 筆客戶資訊`);
          }
        } catch (err) {
          console.error('❌ 批次查詢客戶資訊異常:', err);
        }
      }

      const jobs: EmployeeJob[] = mergedJobs.map((job: any) => {
        const pricingMode: 'hourly' | 'company_fixed' = job.pricing_mode ?? 'hourly';
        const fixedTotalPrice = job.fixed_total_price ?? null;
        const estimatedPrice = job.estimated_price ?? 0;
        const baseAmount =
          pricingMode === 'company_fixed'
            ? (fixedTotalPrice ?? estimatedPrice ?? 0)
            : estimatedPrice ?? 0;
        
        // ✅ 優先使用從付款記錄獲取的實際訂金金額
        const payment = paymentsByJob[job.id] || null;
        const actualDepositAmount = payment?.deposit_amount || 0;
        const actualPaidAmount = payment?.paid_amount || 0;
        
        // 使用實際付款記錄的訂金金額，如果沒有則使用計算值
        const depositAmount = actualDepositAmount > 0 
          ? actualDepositAmount 
          : (pricingMode === 'company_fixed'
            ? Math.max(Math.round(baseAmount * 0.3), PAYMENT_CONFIG.FIXED_DEPOSIT)
              : PAYMENT_CONFIG.FIXED_DEPOSIT);
        
        // ✅ 計算已收訂金（與 getCompanyJobs 邏輯一致）
        const depositReceived = payment && (
          payment.status === 'paid' || 
          payment.status === 'completed' || 
          payment.status === 'approved' ||
          (actualPaidAmount > 0 && actualPaidAmount >= depositAmount) ||
          // ✅ 對於 'pending' 狀態的訂金付款，如果 deposit_amount > 0，也視為已收
          (payment.status === 'pending' && payment.payment_type === 'deposit' && depositAmount > 0)
        );
        
        const receivedDepositAmount = actualPaidAmount > 0 
          ? actualPaidAmount 
          : (depositReceived && depositAmount > 0 ? depositAmount : 0);
        
        const pendingDepositAmount = Math.max(depositAmount - receivedDepositAmount, 0);
        // ✅ 修復：計算剩餘尾款應該基於訂金金額，而不是已收訂金
        const remainingAmount = Math.max(baseAmount - depositAmount, 0);

        // ✅ 保護性處理：在 RLS 或資料缺失的情況下，job.customer 可能為 null
        // 優先使用 customer_name 和 customer_phone（客服手動建立時使用）
        // 如果沒有，則使用關聯的 customer 資料，再沒有則使用批次查詢的資料
        let customerInfo = job.customer;
        let customerFromBatchQuery = null;

        // 如果外鍵關聯失敗但 user_id 存在，使用批次查詢的結果
        if (!customerInfo && job.user_id && customersByUserId[job.user_id]) {
          customerFromBatchQuery = customersByUserId[job.user_id];
        }

        // 優先使用 customer_name 和 customer_phone（客服手動建立時使用）
        // 如果沒有，則使用關聯的 customer 資料，再沒有則使用批次查詢的資料
        const finalCustomerName = job.customer_name || customerInfo?.name || customerFromBatchQuery?.name || null;
        const finalCustomerEmail = customerInfo?.email || customerFromBatchQuery?.email || (job.user_id ? `用戶 ${job.user_id.slice(0, 8)}...` : null) || null;
        const finalCustomerPhone = job.customer_phone || customerInfo?.phone || customerFromBatchQuery?.phone || null;

        // 調試資訊（僅在完全沒有客戶資訊時輸出）
        if (!finalCustomerName && !finalCustomerPhone && !finalCustomerEmail) {
          console.warn('⚠️ 案件缺少客戶資訊:', {
            id: job.id,
            user_id: job.user_id,
            customer_name: job.customer_name,
            customer_phone: job.customer_phone,
            customer_from_join: job.customer,
            customer_from_batch_query: customerFromBatchQuery,
          });
        }

        const safeCustomer = {
          id: customerInfo?.id || customerFromBatchQuery?.id || job.user_id || '',
          name: finalCustomerName || '未知客戶',
          email: finalCustomerEmail || '',
          phone: finalCustomerPhone || ''
        };

        return {
          id: job.id,
          title: job.title,
          description: job.description,
          status: job.status,
          customer: safeCustomer,
          amount: baseAmount || 0,
          fixedTotalPrice,
          pricingMode,
          depositAmount,
          receivedDepositAmount,
          pendingDepositAmount,
          remainingAmount,
          payment: payment,
          paymentStatus: payment?.status || null,
          financeReviewStatus: payment?.finance_review_status || 'pending',
          paidAt: payment?.paid_at ? new Date(payment.paid_at) : null,
          created_at: new Date(job.created_at),
          scheduled_date: job.scheduled_date ? new Date(job.scheduled_date) : undefined,
          started_at: job.started_at ? new Date(job.started_at) : undefined,
          completed_at: job.completed_at ? new Date(job.completed_at) : undefined,
          location: job.location,
          urgency: job.urgency || 'normal',
          notes: job.notes ?? null,
          completion_photos: job.completion_photos ?? [],
          assigned_department: job.assigned_department ?? null,
          assigned_departments: Array.isArray(job.assigned_departments)
            ? job.assigned_departments
            : job.assigned_department
            ? [job.assigned_department]
            : [],
          site_contact_name: job.site_contact_name ?? null,
          site_contact_phone: job.site_contact_phone ?? null,
          schedule_notes: job.schedule_notes ?? null
        };
      });

      // 计算统计
      // 將 pending_quotation_approval 歸類到 pending
      const stats = {
        pending: jobs.filter(j => j.status === 'pending' || j.status === 'pending_quotation_approval').length,
        confirmed: jobs.filter(j => j.status === 'confirmed').length,
        inProgress: jobs.filter(j => j.status === 'in_progress').length,
        completed: jobs.filter(j => j.status === 'completed').length,
        cancelled: jobs.filter(j => j.status === 'cancelled').length,
        total: jobs.length
      };

      return { jobs, stats };
    } catch (error) {
      console.error('获取员工案件时发生错误:', error);
      return { jobs: [], stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0, total: 0 } };
    }
  }

  // 员工接受案件
  static async acceptJob(jobId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      // 获取员工ID
      const employeeInfo = await this.getEmployeeInfo(user?.id ?? '');
      if (!employeeInfo.employee) {
        return { success: false, error: '找不到員工資訊' };
      }

      const { error } = await supabase
        .from('service_requests')
        .update({
          status: 'confirmed',
          accepted_by: employeeInfo.employee.id,
          accepted_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) {
        console.error('接受案件失败:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('接受案件时发生错误:', error);
      return { success: false, error: '接受案件失败' };
    }
  }

  // 员工拒绝案件
  static async rejectJob(jobId: string, reason: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      if (!reason || !reason.trim()) {
        return { success: false, error: '請填寫拒絕原因' };
      }

      // 获取员工ID
      const employeeInfo = await this.getEmployeeInfo(user?.id ?? '');
      if (!employeeInfo.employee) {
        return { success: false, error: '找不到員工資訊' };
      }

      const { error } = await supabase
        .from('service_requests')
        .update({
          status: 'cancelled',
          rejection_reason: reason.trim(),
          rejected_by: employeeInfo.employee.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) {
        console.error('拒絕案件失败:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('拒絕案件时发生错误:', error);
      return { success: false, error: '拒絕案件失败' };
    }
  }

  // 员工开始工作
  static async startJob(jobId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      const existingDepartments = await fetchAssignedDepartments(jobId);
      const departmentSet = new Set<string>(existingDepartments);
      departmentSet.add('維修部');
      const updatedDepartments = Array.from(departmentSet);

      const { error } = await updateServiceRequestWithDepartments(
        jobId,
        {
          status: 'in_progress',
          started_at: new Date().toISOString(),
          assigned_department: '維修部',
          updated_at: new Date().toISOString()
        },
        updatedDepartments
      );

      if (error) {
        console.error('开始工作失败:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('开始工作时发生错误:', error);
      return { success: false, error: '开始工作失败' };
    }
  }

  // 员工完成案件
  static async completeJob(
    jobId: string,
    notes?: string,
    photos?: string[]
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      const existingDepartments = await fetchAssignedDepartments(jobId);
      const departmentSet = new Set<string>(existingDepartments);
      // ✅ 修正：不要刪除維修部，因為維修部需要追蹤保固期的案件
      // departmentSet.delete('維修部'); // 移除這行
      departmentSet.add('財務部'); // 添加財務部處理尾款
      const updatedDepartments = Array.from(departmentSet);

      const updateData: any = {
        status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        assigned_department: '財務部' // 主負責部門設為財務部
      };

      if (notes) {
        updateData.notes = notes;
      }

      if (photos && photos.length > 0) {
        updateData.completion_photos = photos;
      }

      const { error } = await updateServiceRequestWithDepartments(jobId, updateData, updatedDepartments);

      if (error) {
        console.error('完成案件失败:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('完成案件时发生错误:', error);
      return { success: false, error: '完成案件失败' };
    }
  }

  /**
   * 合併新增完工照片 URL 至 service_requests.completion_photos（不重複 URL）。
   */
  static async appendCompletionPhotos(
    jobId: string,
    newUrls: string[]
  ): Promise<{ success: boolean; error: string | null; completion_photos?: string[] }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      const filtered = (newUrls || []).map(u => (typeof u === 'string' ? u.trim() : '')).filter(Boolean);
      if (filtered.length === 0) {
        return { success: true, error: null };
      }

      const { data: row, error: fetchError } = await supabase
        .from('service_requests')
        .select('completion_photos')
        .eq('id', jobId)
        .maybeSingle();

      if (fetchError) {
        console.error('讀取完工照片失敗:', fetchError);
        return { success: false, error: fetchError.message };
      }

      const existing = Array.isArray(row?.completion_photos) ? (row!.completion_photos as string[]) : [];
      const seen = new Set<string>();
      const merged: string[] = [];
      for (const u of existing) {
        const s = typeof u === 'string' ? u.trim() : '';
        if (s && !seen.has(s)) {
          seen.add(s);
          merged.push(s);
        }
      }
      for (const u of filtered) {
        if (!seen.has(u)) {
          seen.add(u);
          merged.push(u);
        }
      }

      const { error } = await supabase
        .from('service_requests')
        .update({
          completion_photos: merged,
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (error) {
        console.error('更新完工照片失敗:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null, completion_photos: merged };
    } catch (error: any) {
      console.error('appendCompletionPhotos 錯誤:', error);
      return { success: false, error: error?.message || '更新完工照片失敗' };
    }
  }

  // 获取员工信息
  static async getEmployeeInfo(userId: string): Promise<{
    employee: any | null;
    error: string | null;
  }> {
    try {
      console.log('获取员工信息，用户ID:', userId);

      // 檢查當前認證狀態
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      const { data: { user: currentAuthUser }, error: authError } = await supabase.auth.getUser();
      
      const authUserId = currentAuthUser?.id || session?.user?.id || null;
      const effectiveUserId = authUserId || userId;
      
      if (sessionError || authError || !session) {
        console.error('❌ 沒有有效的 Auth session！');
        console.error('這會導致 RLS 策略無法正常工作');
        console.error('Session 錯誤:', sessionError);
        console.error('Auth 錯誤:', authError);
        console.warn('請確保用戶已通過 Supabase Auth 登入');
        console.warn('如果使用 localLogin，請確保該方法成功創建了 Auth session');
      } else {
        console.log('✅ Auth session 存在');
        console.log('當前認證用戶ID:', currentAuthUser?.id);
        console.log('目標用戶ID:', effectiveUserId);
        console.log('ID匹配:', currentAuthUser?.id === effectiveUserId);
        console.log('Session 到期時間:', session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'N/A');
      }

      // 第一步：获取基本员工信息（不使用关联查询，避免 RLS 问题）
      let employeeData = null;
      let employeeError = null;
      
      // 首先获取用户 email（用于备用查询）
      // 優先從 Auth session 獲取 email，如果失敗再查詢 users 表
      let userEmail: string | undefined;
      
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser?.email) {
          userEmail = authUser.email;
          console.log('✅ 從 Auth session 獲取 email:', userEmail);
        }
      } catch (authErr) {
        console.warn('無法從 Auth session 獲取 email:', authErr);
      }
      
      // 如果無法從 Auth session 獲取，嘗試從 users 表查詢
      if (!userEmail) {
        const { data: userEmailData } = await supabase
          .from('users')
          .select('email')
          .eq('id', userId)
          .maybeSingle();
        
        userEmail = userEmailData?.email;
        if (userEmail) {
          console.log('✅ 從 users 表獲取 email:', userEmail);
        }
      }
      
      // 首先尝试通过 user_id 查询（添加重試邏輯處理 502 錯誤）
      let empDataByUserId = null;
      let errorByUserId = null;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (retryCount < maxRetries) {
        const { data, error } = await supabase
          .from('employees')
          .select(`
            id,
            name,
            email,
            phone,
            employee_number,
            department,
            position,
            status,
            company_id,
            hire_date,
            salary,
            specialties,
            certifications,
            experience,
            created_at,
            updated_at,
            user_id
          `)
          .eq('user_id', effectiveUserId)
          .maybeSingle(); // 使用 maybeSingle 而不是 single，避免 0 行時報錯
        
        if (!error || (error.code !== '502' && error.code !== '503' && error.code !== '504')) {
          empDataByUserId = data;
          errorByUserId = error;
          break;
        }
        
        // 如果是 502/503/504 錯誤，等待後重試
        retryCount++;
        if (retryCount < maxRetries) {
          console.warn(`查詢員工信息失敗 (${error.code})，重試 ${retryCount}/${maxRetries}...`);
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount)); // 遞增延遲
        } else {
          errorByUserId = error;
        }
      }
      
      if (!errorByUserId && empDataByUserId) {
        employeeData = empDataByUserId;
      } else if (userEmail) {
        // 如果通过 user_id 找不到，尝试通过 email 查询
        // 使用 .limit(1) 來處理可能有多筆記錄的情況
        const { data: empDataByEmailList, error: errorByEmail } = await supabase
          .from('employees')
          .select(`
            id,
            name,
            email,
            phone,
            employee_number,
            department,
            position,
            status,
            company_id,
            hire_date,
            salary,
            specialties,
            certifications,
            experience,
            created_at,
            updated_at,
            user_id
          `)
          .eq('email', userEmail)
          .limit(10); // 限制最多 10 筆，以便處理多筆記錄的情況
        
        if (!errorByEmail && empDataByEmailList && empDataByEmailList.length > 0) {
          // 優先選擇 user_id 匹配的記錄
          let empDataByEmail = empDataByEmailList.find(emp => emp.user_id === userId);
          
          // 如果沒有匹配的，使用第一筆記錄
          if (!empDataByEmail) {
            empDataByEmail = empDataByEmailList[0];
          }
          
          employeeData = empDataByEmail;
          
          // 如果找到記錄但 user_id 不匹配，嘗試更新所有匹配該 email 的記錄的 user_id
          if (authUserId && empDataByEmail.user_id !== authUserId) {
            // 更新所有匹配該 email 的記錄（因為可能有多筆記錄）
            const { data: updatedRecords, error: updateError } = await supabase
              .from('employees')
              .update({ user_id: authUserId, updated_at: new Date().toISOString() })
              .eq('email', userEmail)
              .select();
            
            if (!updateError && updatedRecords && updatedRecords.length > 0) {
              // 重新查詢以獲取更新後的數據（使用 user_id 查詢，應該能找到）
              const { data: updatedData } = await supabase
                .from('employees')
                .select('*')
                .eq('user_id', authUserId)
                .maybeSingle();
              
              if (updatedData) {
                employeeData = updatedData;
              } else {
                // 如果還是找不到，使用第一筆更新後的記錄
                employeeData = updatedRecords[0];
              }
            } else {
              // 詳細記錄更新錯誤
              if (updateError) {
                console.error('❌ 更新 user_id 失敗:', {
                  message: updateError.message,
                  code: updateError.code,
                  details: updateError.details,
                  hint: updateError.hint
                });
                
                // 如果是 RLS 相關錯誤，提供更詳細的信息
                if (updateError.code === '42501' || updateError.message?.includes('permission') || updateError.message?.includes('policy')) {
                  console.error('💡 這是 RLS 策略問題，請確保已執行 migration: 20251104000002_add_employees_self_view_policy.sql');
                  console.error('💡 UPDATE 策略需要允許通過 email 匹配來更新記錄');
                }
              } else {
                console.error('❌ 更新 user_id 失敗: 未知錯誤（更新返回 null 或空結果）');
                console.warn('💡 這可能是因為 RLS 策略不允許更新，或記錄不存在');
              }
              
              // 即使更新失敗，也使用找到的記錄（允許頁面繼續運作）
              console.warn('⚠️ 將使用找到的記錄（user_id 可能不匹配）');
            }
          } else if (!authUserId) {
            console.warn('⚠️ 未取得有效的 Auth user ID，跳過 user_id 自動更新檢查');
          }
        } else {
          employeeError = errorByEmail || errorByUserId;
          console.warn('通過 email 也找不到員工記錄:', employeeError);
        }
      } else {
        employeeError = errorByUserId;
        console.warn('無法獲取用戶 email，無法進行備用查詢');
      }

      if (employeeError) {
        console.error('获取员工信息失败 - 錯誤詳情:', {
          message: employeeError.message,
          code: employeeError.code,
          details: employeeError.details,
          hint: employeeError.hint
        });
        
        // 如果是 RLS 相關錯誤，提供更詳細的信息
        if (employeeError.code === '42501' || employeeError.message.includes('permission') || employeeError.message.includes('policy')) {
          console.error('可能是 RLS 策略問題，請檢查策略設置');
        }
        
        return { employee: null, error: employeeError.message };
      }

      if (!employeeData) {
        console.warn('找不到员工记录，用户ID:', userId);
        return { employee: null, error: '找不到员工记录。请确保该用户在 employees 表中有对应记录。' };
      }

      // 第二步：如果有 company_id，单独获取公司信息
      let companyInfo = null;
      if (employeeData.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('id, company_name')
          .eq('id', employeeData.company_id)
          .maybeSingle();

        if (!companyError && companyData) {
          companyInfo = companyData;
        } else if (companyError) {
          console.warn('获取公司信息失败（可能是 RLS 限制）:', companyError);
          // 不返回错误，因为员工信息已经获取成功
        }
      }

      // 组装返回数据
      const result = {
        ...employeeData,
        company: companyInfo
      };

      return { employee: result, error: null };
    } catch (error: any) {
      console.error('获取员工信息时发生错误:', error);
      return { employee: null, error: error.message || '获取员工信息失败' };
    }
  }

  // 获取员工收入统计
  static async getEmployeeEarnings(employeeId: string): Promise<{
    thisMonthEarnings: number;
    totalEarnings: number;
    monthlyGrowth: number;
    completedJobsCount: number;
  }> {
    try {
      // 首先通过 user_id 查找员工记录
      let employeeData = null;
      
      const { data: empDataByUserId, error: errorByUserId } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', employeeId)
        .maybeSingle();
      
      if (!errorByUserId && empDataByUserId) {
        employeeData = empDataByUserId;
      } else {
        // 尝试通过 email 查询
        const { data: userData } = await supabase
          .from('users')
          .select('email')
          .eq('id', employeeId)
          .maybeSingle();
        
        if (userData?.email) {
          const { data: empDataByEmail } = await supabase
            .from('employees')
            .select('id')
            .eq('email', userData.email)
            .maybeSingle();
          
          if (empDataByEmail) {
            employeeData = empDataByEmail;
          }
        }
      }

      if (!employeeData) {
        return { thisMonthEarnings: 0, totalEarnings: 0, monthlyGrowth: 0, completedJobsCount: 0 };
      }

      const actualEmployeeId = employeeData.id;

      // 获取本月开始的日期
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);

      // 获取所有已完成的案件
      const { data: completedJobsDirect, error } = await supabase
        .from('service_requests')
        .select('id, estimated_price, completed_at, created_at')
        .eq('assigned_employee_id', actualEmployeeId)
        .eq('status', 'completed');

      const jobEarningsMap = new Map<string, any>();
      (completedJobsDirect || []).forEach(job => {
        if (job?.id) jobEarningsMap.set(job.id, job);
      });

      const { data: assignmentRecords, error: assignmentError } = await supabase
        .from('service_request_assignments')
        .select('service_request_id')
        .eq('employee_id', actualEmployeeId);

      if (!assignmentError && assignmentRecords?.length) {
        const additionalJobIds = assignmentRecords
          .map((record: any) => record?.service_request_id)
          .filter((id: string | null | undefined) => !!id && !jobEarningsMap.has(id));

        if (additionalJobIds.length > 0) {
          const { data: additionalJobs, error: additionalError } = await supabase
            .from('service_requests')
            .select('id, estimated_price, completed_at, created_at')
            .in('id', additionalJobIds)
            .eq('status', 'completed');

          if (additionalError) {
            console.error('取得指派完成案件失敗:', additionalError);
          } else {
            (additionalJobs || []).forEach(job => {
              if (job?.id) jobEarningsMap.set(job.id, job);
            });
          }
        }
      }

      if (error) {
        console.error('获取员工收入失败:', error);
        return { thisMonthEarnings: 0, totalEarnings: 0, monthlyGrowth: 0, completedJobsCount: 0 };
      }

      const jobs = Array.from(jobEarningsMap.values());
      const completedJobsCount = jobs.length;

      // 计算本月收入
      const thisMonthJobs = jobs.filter((job: any) => {
        if (!job.completed_at) return false;
        const completedDate = new Date(job.completed_at);
        return completedDate >= startOfMonth;
      });
      const thisMonthEarnings = thisMonthJobs.reduce((sum: number, job: any) => sum + (job.estimated_price || 0), 0);

      // 计算上月收入（用于计算增长率）
      const lastMonthJobs = jobs.filter((job: any) => {
        if (!job.completed_at) return false;
        const completedDate = new Date(job.completed_at);
        return completedDate >= startOfLastMonth && completedDate <= endOfLastMonth;
      });
      const lastMonthEarnings = lastMonthJobs.reduce((sum: number, job: any) => sum + (job.estimated_price || 0), 0);

      // 计算总收入
      const totalEarnings = jobs.reduce((sum: number, job: any) => sum + (job.estimated_price || 0), 0);

      // 计算月增长率
      const monthlyGrowth = lastMonthEarnings > 0
        ? ((thisMonthEarnings - lastMonthEarnings) / lastMonthEarnings) * 100
        : (thisMonthEarnings > 0 ? 100 : 0);

      return {
        thisMonthEarnings,
        totalEarnings,
        monthlyGrowth: Math.round(monthlyGrowth * 100) / 100,
        completedJobsCount
      };
    } catch (error) {
      console.error('获取员工收入统计时发生错误:', error);
      return { thisMonthEarnings: 0, totalEarnings: 0, monthlyGrowth: 0, completedJobsCount: 0 };
    }
  }

  // 获取今日任务
  static async getTodayJobs(employeeId: string): Promise<EmployeeJob[]> {
    try {
      // 首先通过 user_id 查找员工记录
      let employeeData = null;
      
      const { data: empDataByUserId, error: errorByUserId } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', employeeId)
        .maybeSingle();
      
      if (!errorByUserId && empDataByUserId) {
        employeeData = empDataByUserId;
      } else {
        // 尝试通过 email 查询
        const { data: userData } = await supabase
          .from('users')
          .select('email')
          .eq('id', employeeId)
          .maybeSingle();
        
        if (userData?.email) {
          const { data: empDataByEmail } = await supabase
            .from('employees')
            .select('id')
            .eq('email', userData.email)
            .maybeSingle();
          
          if (empDataByEmail) {
            employeeData = empDataByEmail;
          }
        }
      }

      if (!employeeData) {
        return [];
      }

      const actualEmployeeId = employeeData.id;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // 获取今日和即将到来的案件
      const { data: directJobs, error } = await selectServiceRequestsWithFallback((fields) =>
        supabase
          .from('service_requests')
          .select(fields)
          .eq('assigned_employee_id', actualEmployeeId)
          .in('status', ['pending', 'confirmed', 'in_progress'])
      );

      if (error) {
        console.error('获取今日任务失败:', error);
        return [];
      }

      const jobMap = new Map<string, any>();
      (directJobs || []).forEach((job: any) => {
        if (job?.id) {
          jobMap.set(job.id, job);
        }
      });

      const { data: assignmentRecords, error: assignmentError } = await supabase
        .from('service_request_assignments')
        .select('service_request_id')
        .eq('employee_id', actualEmployeeId);

      if (!assignmentError && assignmentRecords?.length) {
        const additionalJobIds = assignmentRecords
          .map((record: any) => record?.service_request_id)
          .filter((id: string | null | undefined) => !!id && !jobMap.has(id));

        if (additionalJobIds.length > 0) {
          const { data: additionalJobs, error: additionalError } = await selectServiceRequestsWithFallback((fields) =>
            supabase
              .from('service_requests')
              .select(fields)
              .in('id', additionalJobIds)
              .in('status', ['pending', 'confirmed', 'in_progress'])
          );

          if (additionalError) {
            console.error('取得指派任務失敗:', additionalError);
          } else {
            (additionalJobs || []).forEach((job: any) => {
              if (job?.id) {
                jobMap.set(job.id, job);
              }
            });
          }
        }
      }

      const jobs: EmployeeJob[] = Array.from(jobMap.values())
        .filter((job: any) => {
          // 施工中的案件應該始終顯示，無論 scheduled_date 是什麼
          if (job?.status === 'in_progress') {
            return true;
          }
          // 其他狀態的案件：如果有 scheduled_date，只顯示今天或未來的
          if (!job?.scheduled_date) return true;
          const scheduledDate = new Date(job.scheduled_date);
          return scheduledDate >= today;
        })
        .sort((a: any, b: any) => {
          // 優先排序：施工中 > 待確認 > 待排程 > 其他
          const statusPriority: Record<string, number> = {
            'in_progress': 0,
            'pending': 1,
            'confirmed': 2,
            'completed': 3,
            'cancelled': 4
          };
          const priorityA = statusPriority[a?.status] ?? 5;
          const priorityB = statusPriority[b?.status] ?? 5;
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          // 相同優先級時，按日期排序
          const dateA = a?.scheduled_date ? new Date(a.scheduled_date).getTime() : Number.MAX_SAFE_INTEGER;
          const dateB = b?.scheduled_date ? new Date(b.scheduled_date).getTime() : Number.MAX_SAFE_INTEGER;
          return dateA - dateB;
        })
        .map((job: any) => ({
          id: job.id,
          title: job.title,
          description: job.description,
          status: job.status,
          customer: job.customer ? {
            id: job.customer.id,
            name: job.customer.name,
            email: job.customer.email,
            phone: job.customer.phone
          } : {
            id: '',
            name: '未知客戶',
            email: '',
            phone: ''
          },
          amount: job.estimated_price || job.fixed_total_price || 0,
          created_at: new Date(job.created_at),
          scheduled_date: job.scheduled_date ? new Date(job.scheduled_date) : undefined,
          location: job.location,
          urgency: job.urgency || 'normal'
        }));

      return jobs;
    } catch (error) {
      console.error('获取今日任务时发生错误:', error);
      return [];
    }
  }

  // 获取员工绩效指标
  static async getEmployeePerformance(employeeId: string): Promise<{
    completionRate: number;
    averageRating: number;
    responseTime: number;
    totalJobs: number;
  }> {
    try {
      // 首先通过 user_id 查找员工记录
      const { data: employeeData, error: empError } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', employeeId)
        .single();

      if (empError || !employeeData) {
        return { completionRate: 0, averageRating: 0, responseTime: 0, totalJobs: 0 };
      }

      const actualEmployeeId = employeeData.id;

      // 获取所有分配的案件
      const { data: directJobs, error } = await supabase
        .from('service_requests')
        .select('id, status, created_at, assigned_at')
        .eq('assigned_employee_id', actualEmployeeId);

      if (error) {
        console.error('获取员工绩效失败:', error);
        return { completionRate: 0, averageRating: 0, responseTime: 0, totalJobs: 0 };
      }

      const jobMap = new Map<string, any>();
      (directJobs || []).forEach(job => {
        if (job?.id) jobMap.set(job.id, job);
      });

      const { data: assignmentRecords, error: assignmentError } = await supabase
        .from('service_request_assignments')
        .select('service_request_id')
        .eq('employee_id', actualEmployeeId);

      if (!assignmentError && assignmentRecords?.length) {
        const additionalJobIds = assignmentRecords
          .map((record: any) => record?.service_request_id)
          .filter((id: string | null | undefined) => !!id && !jobMap.has(id));

        if (additionalJobIds.length > 0) {
          const { data: additionalJobs, error: additionalError } = await supabase
            .from('service_requests')
            .select('id, status, created_at, assigned_at')
            .in('id', additionalJobIds);

          if (additionalError) {
            console.error('取得指派案件績效資料失敗:', additionalError);
          } else {
            (additionalJobs || []).forEach(job => {
              if (job?.id) jobMap.set(job.id, job);
            });
          }
        }
      }

      const jobs = Array.from(jobMap.values());
      const totalJobs = jobs.length;
      const completedJobs = jobs.filter((j: any) => j.status === 'completed').length;
      const completionRate = totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0;

      // 计算平均响应时间（从分配到确认的时间，以小时为单位）
      const confirmedJobs = jobs.filter((j: any) => j.status === 'confirmed' && j.assigned_at);
      let totalResponseTime = 0;
      confirmedJobs.forEach((job: any) => {
        if (job.assigned_at && job.created_at) {
          const assignedTime = new Date(job.assigned_at).getTime();
          const createdTime = new Date(job.created_at).getTime();
          const hours = (assignedTime - createdTime) / (1000 * 60 * 60);
          totalResponseTime += hours;
        }
      });
      const responseTime = confirmedJobs.length > 0 ? totalResponseTime / confirmedJobs.length : 0;

      // 平均评分（暂时返回0，因为需要从评价表获取）
      const averageRating = 0;

      return {
        completionRate: Math.round(completionRate * 100) / 100,
        averageRating,
        responseTime: Math.round(responseTime * 100) / 100,
        totalJobs
      };
    } catch (error) {
      console.error('获取员工绩效指标时发生错误:', error);
      return { completionRate: 0, averageRating: 0, responseTime: 0, totalJobs: 0 };
    }
  }

  // 获取员工收入趋势
  static async getEmployeeEarningsTrend(employeeId: string, months: number = 6): Promise<any[]> {
    try {
      // 首先通过 user_id 查找员工记录
      let employeeData = null;
      
      const { data: empDataByUserId, error: errorByUserId } = await supabase
        .from('employees')
        .select('id')
        .eq('user_id', employeeId)
        .maybeSingle();
      
      if (!errorByUserId && empDataByUserId) {
        employeeData = empDataByUserId;
      } else {
        // 尝试通过 email 查询
        const { data: userData } = await supabase
          .from('users')
          .select('email')
          .eq('id', employeeId)
          .maybeSingle();
        
        if (userData?.email) {
          const { data: empDataByEmail } = await supabase
            .from('employees')
            .select('id')
            .eq('email', userData.email)
            .maybeSingle();
          
          if (empDataByEmail) {
            employeeData = empDataByEmail;
          }
        }
      }

      if (!employeeData) {
        return [];
      }

      const actualEmployeeId = employeeData.id;
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);

      // 获取最近几个月的已完成案件
      const { data: directJobs, error } = await supabase
        .from('service_requests')
        .select('id, estimated_price, completed_at')
        .eq('assigned_employee_id', actualEmployeeId)
        .eq('status', 'completed')
        .gte('completed_at', startDate.toISOString())
        .order('completed_at', { ascending: true });

      if (error) {
        console.error('获取收入趋势失败:', error);
        return [];
      }

      const jobMap = new Map<string, any>();
      (directJobs || []).forEach(job => {
        if (job?.id) jobMap.set(job.id, job);
      });

      const { data: assignmentRecords, error: assignmentError } = await supabase
        .from('service_request_assignments')
        .select('service_request_id')
        .eq('employee_id', actualEmployeeId);

      if (!assignmentError && assignmentRecords?.length) {
        const additionalJobIds = assignmentRecords
          .map((record: any) => record?.service_request_id)
          .filter((id: string | null | undefined) => !!id && !jobMap.has(id));

        if (additionalJobIds.length > 0) {
          const { data: additionalJobs, error: additionalError } = await supabase
            .from('service_requests')
            .select('id, estimated_price, completed_at')
            .in('id', additionalJobIds)
            .eq('status', 'completed')
            .gte('completed_at', startDate.toISOString())
            .order('completed_at', { ascending: true });

          if (additionalError) {
            console.error('取得指派案件收入資料失敗:', additionalError);
          } else {
            (additionalJobs || []).forEach(job => {
              if (job?.id) jobMap.set(job.id, job);
            });
          }
        }
      }

      // 按月份分组统计
      const monthlyData: { [key: string]: number } = {};
      const jobs = Array.from(jobMap.values());

      jobs.forEach((job: any) => {
        if (job.completed_at) {
          const date = new Date(job.completed_at);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (!monthlyData[monthKey]) {
            monthlyData[monthKey] = 0;
          }
          monthlyData[monthKey] += job.estimated_price || 0;
        }
      });

      // 生成最近几个月的完整数据
      const trendData = [];
      for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = `${date.getMonth() + 1}月`;
        trendData.push({
          month: monthLabel,
          earnings: monthlyData[monthKey] || 0,
          expenses: 0
        });
      }

      return trendData;
    } catch (error) {
      console.error('获取收入趋势时发生错误:', error);
      return [];
    }
  }

  /**
   * 「財務已核准」且已入帳之付款列（公司維度）：
   * - payments.company_id = 公司，或
   * - 透過 request 關聯 service_requests.company_id = 公司（含 company_id 未寫入 payments 的舊資料）
   * 依 payments.id 去重後回傳。
   */
  static async getCompanyApprovedFinancePaymentRows(companyId: string): Promise<any[]> {
    const [r1, r2] = await Promise.all([
      supabase
        .from('payments')
        .select(COMPANY_FINANCE_PAYMENT_SELECT)
        .eq('company_id', companyId)
        .eq('finance_review_status', 'approved')
        .in('status', ['paid', 'completed', 'approved']),
      supabase
        .from('payments')
        .select(
          `${COMPANY_FINANCE_PAYMENT_SELECT}, service_requests!inner(company_id)`
        )
        .eq('service_requests.company_id', companyId)
        .eq('finance_review_status', 'approved')
        .in('status', ['paid', 'completed', 'approved'])
    ]);

    if (r1.error && r2.error) {
      console.error('getCompanyApprovedFinancePaymentRows:', r1.error, r2.error);
      return [];
    }
    if (r1.error) {
      console.warn('getCompanyApprovedFinancePaymentRows（company_id）:', r1.error);
    }
    if (r2.error) {
      console.warn('getCompanyApprovedFinancePaymentRows（工單公司）:', r2.error);
    }
    return mergePaymentRowsById(r1.data, r2.data);
  }

  /**
   * 財務儀表板：依公司彙總「財務已核准」且已入帳之付款，按月統計實收金額；
   * 收入納入：(1) payments.company_id 相符；(2) 工單所屬公司相符（與 getCompanyApprovedFinancePaymentRows 同口徑）。
   * 並併入已核准 expenses + other_transactions（type=expense）之月度支出；
   * 另併入維修「現場回報」材料清單小計（quantity×單價，依回報 created_at 歸月），供現金流圖表與現場支出對齊。
   */
  static async getCompanyFinanceEarningsTrend(companyId: string, months: number = 6): Promise<any[]> {
    try {
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - months + 1, 1);
      const startDateIso = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
        0,
        0,
        0,
        0
      ).toISOString();

      const sumOnSiteMaterialList = (materialList: unknown): number => {
        if (!Array.isArray(materialList)) return 0;
        return materialList.reduce((sum: number, item: any) => {
          const qty = Number(item?.quantity ?? 0);
          const unit = Number(item?.cost ?? 0);
          const line = (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unit) ? unit : 0);
          return sum + (line > 0 ? line : 0);
        }, 0);
      };

      const [payments, { data: expenseRows, error: expensesError }, { data: otherExpenseRows, error: otherExpError }, { data: onSiteRows, error: onSiteError }] =
        await Promise.all([
          EmployeeJobService.getCompanyApprovedFinancePaymentRows(companyId),
          supabase
            .from('expenses')
            .select('amount, expense_date')
            .eq('company_id', companyId)
            .eq('status', 'approved'),
          supabase
            .from('other_transactions')
            .select('amount, transaction_date')
            .eq('company_id', companyId)
            .eq('type', 'expense')
            .eq('status', 'approved'),
          supabase
            .from('on_site_reports')
            .select('created_at, material_list, service_requests!inner(company_id)')
            .eq('service_requests.company_id', companyId)
            .gte('created_at', startDateIso)
        ]);
      if (expensesError) {
        console.warn('取得公司支出趨勢（expenses）失敗:', expensesError);
      }
      if (otherExpError) {
        console.warn('取得公司支出趨勢（other_transactions）失敗:', otherExpError);
      }
      if (onSiteError) {
        console.warn('取得公司支出趨勢（現場回報材料）失敗:', onSiteError);
      }

      const monthlyData: Record<string, number> = {};
      (payments || []).forEach((p: any) => {
        const d = companyFinancePaymentRecognizedAt(p);
        if (!d || d < startDate) return;
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        // ✅ 與公司儀表板／公司收入頁一致：趨勢收入使用扣佣後淨額（優先 craftsman_earnings，部分付款按比例分攤）
        const amt = companyFinancePaymentNetAmount(p);
        if (amt <= 0) return;
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + amt;
      });

      const monthlyExpenses: Record<string, number> = {};
      const addExpenseToMonth = (amount: unknown, rawDate: string | null | undefined) => {
        if (!rawDate) return;
        const d = new Date(rawDate);
        if (Number.isNaN(d.getTime()) || d < startDate) return;
        const amt = Number(amount || 0);
        if (amt <= 0) return;
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        monthlyExpenses[monthKey] = (monthlyExpenses[monthKey] || 0) + amt;
      };

      (expenseRows || []).forEach((row: { amount?: number | null; expense_date?: string | null }) =>
        addExpenseToMonth(row.amount, row.expense_date ?? undefined)
      );
      (otherExpenseRows || []).forEach((row: { amount?: number | null; transaction_date?: string | null }) =>
        addExpenseToMonth(row.amount, row.transaction_date ?? undefined)
      );

      (onSiteRows || []).forEach((row: { created_at?: string | null; material_list?: unknown }) => {
        const materials = sumOnSiteMaterialList(row.material_list);
        if (materials <= 0) return;
        addExpenseToMonth(materials, row.created_at ?? undefined);
      });

      const trendData: { month: string; earnings: number; expenses: number }[] = [];
      for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        trendData.push({
          month: `${date.getMonth() + 1}月`,
          earnings: monthlyData[monthKey] || 0,
          expenses: monthlyExpenses[monthKey] || 0
        });
      }

      return trendData;
    } catch (error) {
      console.error('取得公司金流趨勢時發生錯誤:', error);
      return [];
    }
  }

  /**
   * 財務儀表板「本月淨額」扣減項（與現金流趨勢圖支出邏輯對齊）：
   * - 當月 salary_payments（已核可/已付）net_salary
   * - 當月 on_site_reports 材料清單小計
   * - 當月 expenses（已核可），排除 salary_payments.expense_id 指向之列以免與薪資重複扣
   * - 當月 other_transactions type=expense（已核可）
   */
  static async getCompanyThisMonthSalaryAndMaterialExpense(
    companyId: string
  ): Promise<{
    salaryTotal: number;
    materialTotal: number;
    expensesTableTotal: number;
    otherExpenseTotal: number;
  }> {
    try {
      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const period = `${y}-${String(m + 1).padStart(2, '0')}`;
      const monthStart = new Date(y, m, 1, 0, 0, 0, 0);
      const nextMonthStart = new Date(y, m + 1, 1, 0, 0, 0, 0);
      const monthStartIso = monthStart.toISOString();
      const nextMonthStartIso = nextMonthStart.toISOString();

      const sumOnSiteMaterialList = (materialList: unknown): number => {
        if (!Array.isArray(materialList)) return 0;
        return materialList.reduce((sum: number, item: any) => {
          const qty = Number(item?.quantity ?? 0);
          const unit = Number(item?.cost ?? 0);
          const line = (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(unit) ? unit : 0);
          return sum + (line > 0 ? line : 0);
        }, 0);
      };

      const [
        { data: salaryRows, error: salaryErr },
        { data: onSiteRows, error: onSiteErr },
        { data: expenseRows, error: expenseErr },
        { data: otherExpenseRows, error: otherErr }
      ] = await Promise.all([
        supabase
          .from('salary_payments')
          .select('net_salary, status, expense_id')
          .eq('company_id', companyId)
          .eq('period', period)
          .in('status', ['approved', 'paid']),
        supabase
          .from('on_site_reports')
          .select('created_at, material_list, service_requests!inner(company_id)')
          .eq('service_requests.company_id', companyId)
          .gte('created_at', monthStartIso)
          .lt('created_at', nextMonthStartIso),
        supabase
          .from('expenses')
          .select('id, amount, expense_date')
          .eq('company_id', companyId)
          .eq('status', 'approved')
          .gte('expense_date', monthStartIso)
          .lt('expense_date', nextMonthStartIso),
        supabase
          .from('other_transactions')
          .select('amount, transaction_date')
          .eq('company_id', companyId)
          .eq('type', 'expense')
          .eq('status', 'approved')
          .gte('transaction_date', monthStartIso)
          .lt('transaction_date', nextMonthStartIso)
      ]);

      if (salaryErr) console.warn('取得本月薪資支出失敗:', salaryErr);
      if (onSiteErr) console.warn('取得本月現場材料支出失敗:', onSiteErr);
      if (expenseErr) console.warn('取得本月 expenses 失敗:', expenseErr);
      if (otherErr) console.warn('取得本月 other_transactions 支出失敗:', otherErr);

      const salaryExpenseIds = new Set<string>();
      for (const row of salaryRows ?? []) {
        const eid = row.expense_id as string | null | undefined;
        if (eid) salaryExpenseIds.add(eid);
      }

      const salaryTotal =
        (salaryRows ?? []).reduce((sum, row) => sum + Number(row.net_salary ?? 0), 0) || 0;

      let materialTotal = 0;
      for (const row of onSiteRows ?? []) {
        materialTotal += sumOnSiteMaterialList(row.material_list);
      }

      const expensesTableTotal =
        (expenseRows ?? []).reduce((sum, row) => {
          const id = row.id as string | undefined;
          if (id && salaryExpenseIds.has(id)) return sum;
          return sum + Number(row.amount ?? 0);
        }, 0) || 0;

      const otherExpenseTotal =
        (otherExpenseRows ?? []).reduce((sum, row) => sum + Number(row.amount ?? 0), 0) || 0;

      return { salaryTotal, materialTotal, expensesTableTotal, otherExpenseTotal };
    } catch (e) {
      console.error('getCompanyThisMonthSalaryAndMaterialExpense:', e);
      return {
        salaryTotal: 0,
        materialTotal: 0,
        expensesTableTotal: 0,
        otherExpenseTotal: 0
      };
    }
  }

  // 批量接受案件
  static async batchAcceptJobs(jobIds: string[]): Promise<{ success: number; failed: number; errors: string[] }> {
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const jobId of jobIds) {
      const result = await this.acceptJob(jobId);
      if (result.success) {
        success++;
      } else {
        failed++;
        errors.push(`${jobId}: ${result.error}`);
      }
    }

    return { success, failed, errors };
  }

  // 批量更新案件狀態
  static async batchUpdateJobStatus(jobIds: string[], status: string): Promise<{ success: number; failed: number; errors: string[] }> {
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const jobId of jobIds) {
      try {
        const { error } = await supabase
          .from('service_requests')
          .update({ status })
          .eq('id', jobId);

        if (error) {
          failed++;
          errors.push(`${jobId}: ${error.message}`);
        } else {
          success++;
        }
      } catch (error: any) {
        failed++;
        errors.push(`${jobId}: ${error.message}`);
      }
    }

    return { success, failed, errors };
  }

  // 獲取客戶歷史服務記錄
  static async getCustomerHistory(customerId: string): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          status,
          estimated_price,
          created_at,
          completed_at
        `)
        .eq('user_id', customerId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.error('獲取客戶歷史記錄失敗:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('獲取客戶歷史記錄時發生錯誤:', error);
      return [];
    }
  }

  static async getFollowUpsForJobs(jobIds: string[]): Promise<Record<string, WarrantyFollowUp[]>> {
    if (!jobIds || jobIds.length === 0) {
      return {};
    }

    try {
      const { data, error } = await supabase
        .from('service_request_followups')
        .select(`
          id,
          service_request_id,
          employee_id,
          note,
          next_follow_up_date,
          followup_type,
          customer_response,
          attachments,
          created_at,
          employee:employees!service_request_followups_employee_id_fkey (
            id,
            name,
            department
          )
        `)
        .in('service_request_id', jobIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('獲取保固回訪紀錄失敗:', error);
        return {};
      }

      const map: Record<string, WarrantyFollowUp[]> = {};
      (data ?? []).forEach((record: any) => {
        const srId = record.service_request_id as string;
        if (!map[srId]) {
          map[srId] = [];
        }
        const employeeRelation = Array.isArray(record.employee) ? record.employee[0] : record.employee;
        map[srId].push({
          id: record.id,
          service_request_id: record.service_request_id,
          employee_id: record.employee_id,
          note: record.note,
          next_follow_up_date: record.next_follow_up_date,
          followup_type: record.followup_type,
          customer_response: record.customer_response,
          attachments: record.attachments || [],
          created_at: record.created_at,
          employee: employeeRelation ?? undefined
        } as WarrantyFollowUp);
      });

      return map;
    } catch (error) {
      console.error('取得保固回訪紀錄時發生錯誤:', error);
      return {};
    }
  }

  static async addFollowUpToJob(
    jobId: string,
    note: string,
    nextFollowUpDate?: string | null,
    employeeId?: string | null,
    options?: {
      followupType?: 'phone' | 'onsite' | 'line' | 'email' | 'other';
      customerResponse?: 'satisfied' | 'minor_issue' | 'major_issue' | 'no_response';
      attachments?: string[];
    }
  ): Promise<{ success: boolean; error: string | null; followUp?: WarrantyFollowUp }> {
    try {
      const payload: Record<string, any> = {
        service_request_id: jobId,
        note
      };

      if (nextFollowUpDate) {
        payload.next_follow_up_date = nextFollowUpDate;
      }
      if (employeeId) {
        payload.employee_id = employeeId;
      }
      if (options?.followupType) {
        payload.followup_type = options.followupType;
      }
      if (options?.customerResponse) {
        payload.customer_response = options.customerResponse;
      }
      if (options?.attachments && options.attachments.length > 0) {
        payload.attachments = options.attachments;
      }

      const { data, error } = await supabase
        .from('service_request_followups')
        .insert(payload)
        .select(`
          id,
          service_request_id,
          employee_id,
          note,
          next_follow_up_date,
          followup_type,
          customer_response,
          attachments,
          created_at,
          employee:employees!service_request_followups_employee_id_fkey (
            id,
            name,
            department
          )
        `)
        .single();

      if (error) {
        console.error('新增保固回訪紀錄失敗:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        error: null,
        followUp: {
          id: data.id,
          service_request_id: data.service_request_id,
          employee_id: data.employee_id,
          note: data.note,
          next_follow_up_date: data.next_follow_up_date,
          followup_type: data.followup_type,
          customer_response: data.customer_response,
          attachments: data.attachments || [],
          created_at: data.created_at,
          employee: (Array.isArray(data.employee) ? data.employee[0] : data.employee) ?? undefined
        }
      };
    } catch (error) {
      console.error('新增保固回訪紀錄時發生錯誤:', error);
      return { success: false, error: '新增保固回訪紀錄失敗' };
    }
  }

  static async getSchedulesForJobs(jobIds: string[]): Promise<Record<string, JobSchedule | null>> {
    if (!jobIds?.length) return {};
    try {
      const { data, error } = await supabase
        .from('service_request_schedule')
        .select('*')
        .in('service_request_id', jobIds);

      if (error) {
        console.error('獲取排程資料失敗:', error);
        return {};
      }

      const map: Record<string, JobSchedule | null> = {};
      (data ?? []).forEach((schedule: any) => {
        map[schedule.service_request_id] = schedule as JobSchedule;
      });

      jobIds.forEach(id => {
        if (!map[id]) {
          map[id] = null;
        }
      });

      return map;
    } catch (error) {
      console.error('取得排程資料時發生錯誤:', error);
      return {};
    }
  }

  /**
   * 公司統包案：技師管理「待分配」清單（與行政案件頁邏輯對齊）
   * - 尚未指派：assigned_employee_id 為空，且狀態為 pending／pending_quotation_approval／confirmed
   * - 尚未排程：狀態為 confirmed，且 service_request_schedule 無 scheduled_start_at
   * - 例外：in_progress 仍無 assigned_employee_id（資料異常時仍列出）
   */
  static async getCompanyPendingTechnicianAssignmentJobs(companyId: string): Promise<
    Array<Record<string, unknown> & { pending_reasons: string[] }>
  > {
    if (!companyId) return [];
    try {
      const { data: rows, error } = await supabase
        .from('service_requests')
        .select(
          'id,title,description,status,assigned_employee_id,company_id,address,location,created_at,pricing_mode,urgency,estimated_hours'
        )
        .eq('company_id', companyId)
        .eq('pricing_mode', 'company_fixed')
        .in('status', ['pending', 'pending_quotation_approval', 'confirmed', 'in_progress'])
        .order('created_at', { ascending: false });

      if (error) {
        console.error('getCompanyPendingTechnicianAssignmentJobs 查詢失敗:', error);
        return [];
      }

      const jobs = (rows || []) as any[];
      const confirmedIds = jobs.filter((j) => j.status === 'confirmed').map((j: any) => j.id as string);
      const schedules = await this.getSchedulesForJobs(confirmedIds);

      const out: Array<Record<string, unknown> & { pending_reasons: string[] }> = [];
      const seen = new Set<string>();

      for (const job of jobs) {
        if (!job?.id || seen.has(job.id)) continue;
        if (job.status === 'completed' || job.status === 'cancelled') continue;

        const schedule = schedules[job.id] || null;
        const unassigned =
          !job.assigned_employee_id &&
          ['pending', 'pending_quotation_approval', 'confirmed'].includes(job.status);
        const unscheduled =
          job.status === 'confirmed' && (!schedule || !schedule.scheduled_start_at);
        const inProgressMissingAssign = job.status === 'in_progress' && !job.assigned_employee_id;

        if (!unassigned && !unscheduled && !inProgressMissingAssign) continue;

        seen.add(job.id);
        const pending_reasons: string[] = [];
        if (unassigned || inProgressMissingAssign) {
          pending_reasons.push('尚未指派負責員工（assigned_employee_id 為空）');
        }
        if (unscheduled) {
          pending_reasons.push('尚未設定開工／排程（confirmed 且無 scheduled_start_at）');
        }

        const loc =
          job.location && typeof job.location === 'object'
            ? job.location
            : { address: (job.address as string) || '未指定' };

        out.push({
          ...job,
          pending_reasons,
          required_skills: (job as any).required_skills ?? [],
          location: loc,
        });
      }

      return out;
    } catch (e) {
      console.error('getCompanyPendingTechnicianAssignmentJobs 例外:', e);
      return [];
    }
  }

  static async upsertJobSchedule(
    jobId: string,
    payload: Partial<Omit<JobSchedule, 'id' | 'service_request_id' | 'created_at' | 'updated_at'>>
  ): Promise<{ success: boolean; error: string | null; schedule?: JobSchedule }> {
    try {
      const { data, error } = await supabase
        .from('service_request_schedule')
        .upsert({
          service_request_id: jobId,
          scheduled_start_at: payload.scheduled_start_at ?? null,
          scheduled_end_at: payload.scheduled_end_at ?? null,
          assigned_department: payload.assigned_department ?? null,
          assigned_employee_id: payload.assigned_employee_id ?? null,
          site_contact_name: payload.site_contact_name ?? null,
          site_contact_phone: payload.site_contact_phone ?? null,
          notes: payload.notes ?? null
        }, { onConflict: 'service_request_id' })
        .select('*')
        .single();

      if (error) {
        console.error('更新排程失敗:', error);
        return { success: false, error: error.message };
      }

      try {
        const baseUpdate: Record<string, any> = {
          scheduled_date: payload.scheduled_start_at ?? null,
          started_at: payload.scheduled_start_at ?? null,
          completed_at: payload.scheduled_end_at ?? null,
          notes: payload.notes ?? null
        };

        if (payload.assigned_department) {
          const existingDepartments = await fetchAssignedDepartments(jobId);
          const departmentSet = new Set<string>(existingDepartments);
          departmentSet.add(payload.assigned_department);
          const updatedDepartments = Array.from(departmentSet);

          baseUpdate.assigned_department = payload.assigned_department;
          await updateServiceRequestWithDepartments(jobId, baseUpdate, updatedDepartments);
        } else {
          await updateServiceRequestWithDepartments(jobId, baseUpdate);
        }
      } catch (updateMainError) {
        console.warn('同步更新 service_requests 失敗:', updateMainError);
      }

      return { success: true, error: null, schedule: data as JobSchedule };
    } catch (error) {
      console.error('更新排程時發生錯誤:', error);
      return { success: false, error: '更新排程失敗' };
    }
  }

  static async getAttachmentsForJobs(jobIds: string[]): Promise<Record<string, JobAttachment[]>> {
    if (!jobIds?.length) return {};
    try {
      const { data, error } = await supabase
        .from('service_request_attachments')
        .select('*')
        .in('service_request_id', jobIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('取得附件失敗:', error);
        return {};
      }

      const map: Record<string, JobAttachment[]> = {};
      (data ?? []).forEach((attachment: any) => {
        const srId = attachment.service_request_id as string;
        if (!map[srId]) map[srId] = [];
        map[srId].push(attachment as JobAttachment);
      });

      return map;
    } catch (error) {
      console.error('取得附件資料時發生錯誤:', error);
      return {};
    }
  }

  static async uploadAttachment(jobId: string, file: File): Promise<{ success: boolean; error: string | null; url?: string; path?: string; fileType?: string }> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileType = file.type || undefined;
      const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
      const filePath = `${jobId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(ATTACHMENT_BUCKET)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('上傳附件失敗:', uploadError);
        return { success: false, error: uploadError.message };
      }

      const { data } = supabase.storage.from(ATTACHMENT_BUCKET).getPublicUrl(filePath);

      return { success: true, error: null, url: data.publicUrl, path: filePath, fileType };
    } catch (error) {
      console.error('上傳附件時發生錯誤:', error);
      return { success: false, error: '上傳附件失敗' };
    }
  }

  static async addAttachmentRecord(
    jobId: string,
    payload: { url: string; fileName?: string; fileType?: string; description?: string; employeeId?: string | null; storagePath?: string | null }
  ): Promise<{ success: boolean; error: string | null; attachment?: JobAttachment }> {
    try {
      const { data, error } = await supabase
        .from('service_request_attachments')
        .insert({
          service_request_id: jobId,
          file_url: payload.url,
          storage_path: payload.storagePath ?? null,
          file_name: payload.fileName ?? null,
          file_type: payload.fileType ?? null,
          description: payload.description ?? null,
          employee_id: payload.employeeId ?? null
        })
        .select('*')
        .single();

      if (error) {
        console.error('新增附件紀錄失敗:', error);
        return { success: false, error: error.message };
      }

      return { success: true, error: null, attachment: data as JobAttachment };
    } catch (error) {
      console.error('新增附件紀錄時發生錯誤:', error);
      return { success: false, error: '新增附件紀錄失敗' };
    }
  }

  static async deleteAttachment(attachmentId: string, storagePath?: string | null): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('service_request_attachments')
        .delete()
        .eq('id', attachmentId);

      if (error) {
        console.error('刪除附件失敗:', error);
        return { success: false, error: error.message };
      }

      if (storagePath) {
        await EmployeeJobService.removeAttachmentFile(storagePath);
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('刪除附件時發生錯誤:', error);
      return { success: false, error: '刪除附件失敗' };
    }
  }

  static async removeAttachmentFile(path: string): Promise<void> {
    try {
      await supabase.storage.from(ATTACHMENT_BUCKET).remove([path]);
    } catch (error) {
      console.error('移除附件檔案失敗:', error);
    }
  }

  static async assignJobToDepartment(
    jobId: string,
    companyId: string,
    department: string
  ): Promise<{ success: boolean; employeeId?: string | null; error?: string }> {
    try {
      if (!companyId) {
        return { success: false, error: '缺少公司 ID，無法指派案件' };
      }

      const { data: employeeList, error: fetchError } = await supabase
        .from('employees')
        .select('id,status,department,user_id,email')
        .eq('company_id', companyId)
        .order('updated_at', { ascending: false })
        .limit(50);

      if (fetchError) {
        console.error('獲取部門員工失敗:', fetchError);
        return { success: false, error: fetchError.message };
      }

      if (!employeeList || employeeList.length === 0) {
        return { success: false, error: `找不到 ${department} 的員工` };
      }

      const activeEmployees = employeeList.filter(emp => emp?.status === 'active');

      const userTypeMap: Record<string, string> = {};
      const userIds = activeEmployees
        .map(emp => emp?.user_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);

      if (userIds.length > 0) {
        const { data: userRecords, error: userFetchError } = await supabase
          .from('users')
          .select('id,user_type')
          .in('id', userIds);

        if (userFetchError) {
          console.warn('無法取得員工對應的 user_type：', userFetchError);
        } else {
          userRecords?.forEach(record => {
            if (record?.id) {
              userTypeMap[record.id] = record.user_type;
            }
          });
        }
      }

      const departmentEmployee = activeEmployees.find(emp => emp?.department === department);
      const maintenanceEmployee =
        departmentEmployee ??
        activeEmployees.find(emp => userTypeMap[emp.user_id as string] === 'employee' || emp?.department === '維修部') ??
        activeEmployees[0];

      if (!maintenanceEmployee?.id) {
        return { success: false, error: `無法找到可用的員工指派至 ${department}` };
      }

      const employeeId = maintenanceEmployee.id;
      if (!employeeId) {
        return { success: false, error: '部門員工資料不完整，缺少 ID' };
      }

      const { error: assignmentError } = await supabase
        .from('service_request_assignments')
        .upsert(
          {
            service_request_id: jobId,
            employee_id: employeeId,
            role: department,
            assigned_at: new Date().toISOString()
          },
          { onConflict: 'service_request_id,employee_id' }
        );

      if (assignmentError) {
        console.error('新增指派紀錄失敗:', assignmentError);
        return { success: false, error: assignmentError.message };
      }

      try {
        const { error: scheduleUpdateError } = await supabase
          .from('service_request_schedule')
          .update({ assigned_employee_id: employeeId })
          .eq('service_request_id', jobId);

        if (scheduleUpdateError) {
          console.warn('更新排程的指派工程師失敗:', scheduleUpdateError);
        }
      } catch (scheduleUpdateException) {
        console.warn('更新排程指派時發生例外:', scheduleUpdateException);
      }

      const { error: updateError } = await supabase
        .from('service_requests')
        .update({ assigned_employee_id: employeeId, updated_at: new Date().toISOString() })
        .eq('id', jobId);

      if (updateError) {
        console.error('更新案件負責人失敗:', updateError);
        return { success: false, error: updateError.message };
      }

      return { success: true, employeeId };
    } catch (error: any) {
      console.error('指派案件至部門時發生錯誤:', error);
      return { success: false, error: error.message ?? '指派案件失敗' };
    }
  }

  // ==================== 現場回報相關方法 ====================

  // 獲取現場回報列表
  static async getOnSiteReports(jobId: string): Promise<{
    reports: Array<{
      id: string;
      service_request_id: string;
      employee_id: string | null;
      report_text: string | null;
      photos: string[];
      videos: string[];
      material_list: Array<{ name: string; quantity: number; unit: string; cost: number }>;
      created_at: string;
      updated_at: string;
    }>;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('on_site_reports')
        .select('*')
        .eq('service_request_id', jobId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('獲取現場回報失敗:', error);
        return { reports: [], error: error.message };
      }

      return {
        reports: (data || []).map(report => ({
          ...report,
          photos: report.photos || [],
          videos: report.videos || [],
          material_list: (report.material_list || []) as Array<{ name: string; quantity: number; unit: string; cost: number }>
        })),
        error: null
      };
    } catch (error) {
      console.error('獲取現場回報時發生錯誤:', error);
      return { reports: [], error: '獲取現場回報失敗' };
    }
  }

  // 提交現場回報
  static async submitOnSiteReport(
    jobId: string,
    report: {
      text?: string;
      photos?: File[];
      videos?: File[];
      materialList?: Array<{ name: string; quantity: number; unit: string; cost: number }>;
    }
  ): Promise<{ success: boolean; error: string | null; reportId?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      // 获取员工ID
      const employeeInfo = await this.getEmployeeInfo(user?.id ?? '');
      if (!employeeInfo.employee) {
        return { success: false, error: '找不到員工資訊' };
      }

      const materialList = report.materialList ?? [];

      // 先寫入主檔（含材料清單），再上傳檔案並更新網址，避免大影片上傳卡住導致整筆未入庫
      const { data, error } = await supabase
        .from('on_site_reports')
        .insert({
          service_request_id: jobId,
          employee_id: employeeInfo.employee.id,
          report_text: report.text || null,
          photos: [],
          videos: [],
          material_list: materialList
        })
        .select('id')
        .single();

      if (error) {
        console.error('提交現場回報失敗:', error);
        return { success: false, error: error.message };
      }

      const reportId = data.id;
      const photoUrls: string[] = [];
      const videoUrls: string[] = [];

      if (report.photos && report.photos.length > 0) {
        for (const photo of report.photos) {
          const photoUrl = await this.uploadReportFile(jobId, photo, 'photo');
          if (photoUrl) photoUrls.push(photoUrl);
        }
      }

      if (report.videos && report.videos.length > 0) {
        for (const video of report.videos) {
          const videoUrl = await this.uploadReportFile(jobId, video, 'video');
          if (videoUrl) videoUrls.push(videoUrl);
        }
      }

      if (photoUrls.length > 0 || videoUrls.length > 0) {
        const { error: updateError } = await supabase
          .from('on_site_reports')
          .update({ photos: photoUrls, videos: videoUrls })
          .eq('id', reportId);

        if (updateError) {
          console.warn('現場回報主檔已建立，但更新照片／影片網址失敗:', updateError);
        }
      }

      return { success: true, error: null, reportId };
    } catch (error) {
      console.error('提交現場回報時發生錯誤:', error);
      return { success: false, error: '提交現場回報失敗' };
    }
  }

  // 上傳現場回報文件（照片或影片）
  private static async uploadReportFile(
    jobId: string,
    file: File,
    type: 'photo' | 'video'
  ): Promise<string | null> {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `on-site-reports/${jobId}/${type}s/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(ATTACHMENT_BUCKET)
        .upload(filePath, file);

      if (uploadError) {
        console.error(`上傳${type === 'photo' ? '照片' : '影片'}失敗:`, uploadError);
        return null;
      }

      const { data } = supabase.storage.from(ATTACHMENT_BUCKET).getPublicUrl(filePath);
      return data.publicUrl;
    } catch (error) {
      console.error(`上傳${type === 'photo' ? '照片' : '影片'}時發生錯誤:`, error);
      return null;
    }
  }

  // ==================== 追加報價相關方法 ====================

  // 獲取追加報價列表
  static async getAdditionalQuotations(jobId: string): Promise<{
    quotations: Array<{
      id: string;
      service_request_id: string;
      employee_id: string | null;
      amount: number;
      reason: string;
      status: 'pending' | 'approved' | 'rejected' | 'cancelled';
      approved_by: string | null;
      approved_at: string | null;
      rejected_by: string | null;
      rejected_at: string | null;
      rejection_reason: string | null;
      created_at: string;
      updated_at: string;
    }>;
    error: string | null;
  }> {
    try {
      const { data, error } = await supabase
        .from('additional_quotations')
        .select('*')
        .eq('service_request_id', jobId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('獲取追加報價失敗:', error);
        return { quotations: [], error: error.message };
      }

      return { quotations: data || [], error: null };
    } catch (error) {
      console.error('獲取追加報價時發生錯誤:', error);
      return { quotations: [], error: '獲取追加報價失敗' };
    }
  }

  // 提交追加報價
  static async submitAdditionalQuotation(
    jobId: string,
    amount: number,
    reason: string,
    options?: {
      // 若為保固外追加報價，可關聯到對應的保固問題，並在 reason 前加上提示前綴
      warrantyIssueId?: string;
      isWarrantyOutOfScope?: boolean;
    }
  ): Promise<{ success: boolean; error: string | null; quotationId?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      if (!reason || !reason.trim()) {
        return { success: false, error: '請填寫追加報價原因' };
      }

      if (amount <= 0) {
        return { success: false, error: '追加報價金額必須大於0' };
      }

      // 获取员工ID
      const employeeInfo = await this.getEmployeeInfo(user?.id ?? '');
      if (!employeeInfo.employee) {
        return { success: false, error: '找不到員工資訊' };
      }

      // 檢查是否已有待審批的追加報價
      const { data: existingQuotations } = await supabase
        .from('additional_quotations')
        .select('id')
        .eq('service_request_id', jobId)
        .eq('status', 'pending');

      if (existingQuotations && existingQuotations.length > 0) {
        return { success: false, error: '已有待審批的追加報價，請等待審批完成' };
      }

      let finalReason = reason.trim();
      if (options?.isWarrantyOutOfScope) {
        // 在原因前加上【保固外】標記，讓財務一眼看出來
        finalReason = `【保固外】${finalReason}`;
      }

      const { data, error } = await supabase
        .from('additional_quotations')
        .insert({
          service_request_id: jobId,
          employee_id: employeeInfo.employee.id,
          amount,
          reason: finalReason,
          warranty_issue_id: options?.warrantyIssueId ?? null,
          status: 'pending',
          customer_response_status: 'pending'
        })
        .select('id')
        .single();

      if (error) {
        console.error('提交追加報價失敗:', error);
        return { success: false, error: error.message };
      }

      // 更新工單狀態為等待追加報價確認
      await supabase
        .from('service_requests')
        .update({
          status: 'pending_quotation_approval',
          updated_at: new Date().toISOString()
        })
        .eq('id', jobId);

      return { success: true, error: null, quotationId: data.id };
    } catch (error) {
      console.error('提交追加報價時發生錯誤:', error);
      return { success: false, error: '提交追加報價失敗' };
    }
  }

  // 審批追加報價（財務/客服使用）
  static async approveAdditionalQuotation(
    quotationId: string,
    approved: boolean,
    rejectionReason?: string
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await BlacklistGuard.assertUserAllowed(user?.id ?? null);

      // 獲取追加報價信息
      const { data: quotation, error: fetchError } = await supabase
        .from('additional_quotations')
        .select('service_request_id, status')
        .eq('id', quotationId)
        .single();

      if (fetchError || !quotation) {
        return { success: false, error: '找不到追加報價記錄' };
      }

      if (quotation.status !== 'pending') {
        return { success: false, error: '此追加報價已審批，無法重複審批' };
      }

      const updateData: any = {
        status: approved ? 'approved' : 'rejected',
        updated_at: new Date().toISOString()
      };

      if (approved) {
        updateData.approved_by = user?.id ?? null;
        updateData.approved_at = new Date().toISOString();
      } else {
        updateData.rejected_by = user?.id ?? null;
        updateData.rejected_at = new Date().toISOString();
        if (rejectionReason) {
          updateData.rejection_reason = rejectionReason.trim();
        }
      }

      const { error } = await supabase
        .from('additional_quotations')
        .update(updateData)
        .eq('id', quotationId);

      if (error) {
        console.error('審批追加報價失敗:', error);
        return { success: false, error: error.message };
      }

      // 更新工單狀態
      const newStatus = approved ? 'in_progress' : 'pending';
      await supabase
        .from('service_requests')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', quotation.service_request_id);

      return { success: true, error: null };
    } catch (error) {
      console.error('審批追加報價時發生錯誤:', error);
      return { success: false, error: '審批追加報價失敗' };
    }
  }
}

